import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Dispatch, SetStateAction } from 'react';

import Tag from '~/shared/components/Tag';
import { Volume } from '~/shared/types/api';
import { DatafySwitchVolume } from '~/shared/context/DatafySwitchProvider';
import { formatBytes } from '~/shared/utils/formatNumber';
import { Account } from '~/shared/utils/types';
import { resolveVolumeCurrentSizeBytes } from '~/shared/utils/exportUtils';
import SizeCell from '~/shared/components/SizeCell';
import { SkeletonCellRenderer } from '~/shared/components/Table/SkeletonCellRenderer';
import LinkIcon from '~/shared/assets/icons/link-gray.svg';
import Tooltip from '~/shared/components/Tooltip';

import VolumeIdCellRenderer from './VolumeIdCellRenderer';
import VolumeTagsRenderer from './VolumeTagsRenderer';
import AutoScaleRenderer from './AutoScaleRenderer';
// import VolumeActionsCellRenderer from './VolumeActionsCellRenderer';
import Typography from '~/shared/components/Typography';

type VolumeWithSkeleton = Volume & { _isSkeleton?: boolean };

export const getColumnDefs = ({
  sortField,
  setSelectedVolumes,
  // datafySwitchVolumes,
  windowWidth,
  isOrgView,
  accounts,
  canWrite,
}: {
  onSelectAllRows: VoidFunction;
  sortField: { field: string; dir: 'desc' | 'asc' };
  selectedVolumes: string[];
  setSelectedVolumes: Dispatch<SetStateAction<string[]>>;
  datafySwitchVolumes: DatafySwitchVolume[];
  windowWidth: number;
  isOrgView?: boolean;
  accounts?: Account[];
  canWrite: boolean;
}): ColDef<VolumeWithSkeleton>[] => {
  const isSmallScreen = windowWidth < 1440;
  const tagsColumnWidth = isSmallScreen ? 234 : 401;
  const maxVisibleTags = isSmallScreen ? 1 : 2;

  const columns: ColDef<Volume>[] = [
    {
      field: 'volumeId',
      headerName: 'Original Volume ID',
      sort: sortField?.field === 'volumeId' ? sortField?.dir : null,
      initialWidth: 244,
      minWidth: 244,
      maxWidth: 244,
      pinned: 'left',
      cellRenderer: ({ data }: ICellRendererParams<VolumeWithSkeleton>) => {
        if (data && '_isSkeleton' in data && data._isSkeleton) {
          return (
            <div className="flex gap-2 items-center">
              <SkeletonCellRenderer width={170} />
              <LinkIcon className="size-5 text-gray-300" />
            </div>
          );
        }
        return data?.volumeId ? (
          <VolumeIdCellRenderer
            id={data.volumeId}
            accountId={isOrgView ? data.accountId : undefined}
          />
        ) : null;
      },
    },
    {
      field: 'instanceId',
      headerName: 'Instance ID',
      sort: sortField?.field === 'instanceId' ? sortField?.dir : null,
      initialWidth: 204,
      minWidth: 204,
      cellRenderer: ({ data }: ICellRendererParams<VolumeWithSkeleton>) => {
        if (data && '_isSkeleton' in data && data._isSkeleton) {
          return <SkeletonCellRenderer width={150} />;
        }
        return data?.instanceId ? (
          <Tag showFullText text={data.instanceId} variant="instance" />
        ) : null;
      },
    },
    {
      field: 'datafyDiskSize',
      headerName: 'Current Size',
      sort: sortField?.field === 'datafyDiskSize' ? sortField?.dir : null,
      initialWidth: 134,
      minWidth: 134,
      cellRenderer: ({ data }: ICellRendererParams<VolumeWithSkeleton>) => {
        if (data && '_isSkeleton' in data && data._isSkeleton) {
          return <SkeletonCellRenderer width={70} />;
        }
        if (!data) {
          return 'N/A';
        }
        const sizeToUse = resolveVolumeCurrentSizeBytes(data);

        return sizeToUse !== undefined && sizeToUse !== null ? (
          <SizeCell size={sizeToUse} />
        ) : (
          'N/A'
        );
      },
    },
    {
      field: 'diskSize',
      headerName: 'Original Size',
      sort: sortField?.field === 'diskSize' ? sortField?.dir : null,
      initialWidth: 140,
      minWidth: 140,
      valueFormatter: ({ data, value }) =>
        data ? formatBytes((data as Volume).diskSize || 0) : value,
      cellRenderer: ({ data }: ICellRendererParams<VolumeWithSkeleton>) => {
        if (data && '_isSkeleton' in data && data._isSkeleton) {
          return <SkeletonCellRenderer width={70} />;
        }
        return data?.diskSize ? <SizeCell size={data.diskSize} /> : 'N/A';
      },
    },
    {
      field: 'utilization',
      headerName: 'Utilization',
      colId: 'utilization',
      sort: sortField?.field === 'utilization' ? sortField?.dir : null,
      minWidth: 144,
      width: 144,
      valueFormatter: ({ data }) => {
        const p = data?.utilization;
        return !p ? 'N/A' : `${Math.round(p)}%`;
      },
      cellRenderer: ({ data }: { data?: VolumeWithSkeleton }) => {
        if (data && '_isSkeleton' in data && data._isSkeleton) {
          return <SkeletonCellRenderer width={70} />;
        }
        const p = data?.utilization;
        return !p ? 'N/A' : `${Math.round(p)}%`;
      },
    },
    {
      field: 'tags',
      headerName: 'Volume Tags',
      sortable: false,
      initialWidth: tagsColumnWidth,
      minWidth: tagsColumnWidth,
      flex: 1,
      valueFormatter: ({ data }) => {
        if (!data?.tags) {
          return '';
        }
        const vol = data as Volume;
        return vol.tags?.map(({ key, value }) => `"${key}":"${value}"`).join(';');
      },
      cellRenderer: (params: ICellRendererParams<VolumeWithSkeleton>) => {
        if (params.data && '_isSkeleton' in params.data && params.data._isSkeleton) {
          return <SkeletonCellRenderer width={280} />;
        }
        return <VolumeTagsRenderer data={params.data} maxVisible={maxVisibleTags} />;
      },
    },
  ];

  if (isOrgView && accounts) {
    columns.push({
      field: 'accountId',
      headerName: 'Account',
      flex: 1,
      sortable: false,
      minWidth: 250,
      maxWidth: 250,
      cellRenderer: ({ data, value }: { data?: VolumeWithSkeleton; value?: string }) => {
        if (data?._isSkeleton) {
          return <SkeletonCellRenderer width={80} />;
        }
        const account = accounts.find((acc) => acc.accountId === value);
        const displayName = account?.accountName || value || '-';
        const tooltipId =
          displayName !== '-' ? `all-volumes-account-${data?.id}` : undefined;

        return (
          <>
            <div className="flex justify-start overflow-hidden" data-tooltip-id={tooltipId}>
              <Typography
                variant="p3-reg"
                fontWeight={500}
                color="text-[#071031]"
                className="text-[14px] truncate"
              >
                {displayName}
              </Typography>
            </div>
            {tooltipId && <Tooltip id={tooltipId} content={displayName} place="top" />}
          </>
        );
      },
    });
  }


  columns.push({
    headerName: 'AutoScale',
    minWidth: 301,
    width: 301,
    maxWidth: 301,
    sortable: false,
    pinned: 'right',
    cellRenderer: ({ data }: ICellRendererParams<VolumeWithSkeleton>) => {
      if (data && '_isSkeleton' in data && data._isSkeleton) {
        return (
          <div className="flex gap-4 items-center">
            <SkeletonCellRenderer width={32} variant="toggle" />
            <SkeletonCellRenderer width={140} height={10} />
          </div>
        );
      }
      return (
        <AutoScaleRenderer
          volume={data!}
          setSelectedVolumes={setSelectedVolumes}
          canWrite={canWrite}
        />
      );
    },
  });

  // columns.push({
  //   minWidth: 56,
  //   width: 56,
  //   maxWidth: 56,
  //   sortable: false,
  //   pinned: 'right',
  //   cellRenderer: ({ data }: ICellRendererParams<VolumeWithSkeleton>) => {
  //     if (data && '_isSkeleton' in data && data._isSkeleton) {
  //       return (
  //         <div className="flex justify-center px-1">
  //           <SkeletonCellRenderer variant="action" />
  //         </div>
  //       );
  //     }

  //     return <VolumeActionsCellRenderer volume={data ?? undefined} />;
  //   },
  // });
  return columns;
};
